import { alpha, createTheme } from "@mui/material";
import LinkWithQuery from "../components/helpers/LinkWithQuery.js";
import breakpoints from "../constants/breakpoints.js";
import palette from "../constants/palette.js";

let theme = createTheme({
  breakpoints: {
    values: {
      ...breakpoints,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0px 16px 32px -4px rgba(12, 12, 13, 0.1), 0px 4px 4px -4px rgba(12, 12, 13, 0.05)",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        disableElevation: true,
        disableTouchRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 45,
          padding: "1rem 2rem",
          fontSize: 21,
          lineHeight: 1.2,
          fontWeight: 700,
          minWidth: 0,
          textTransform: "none",
        },
        contained: {
          border: "none",
          ":hover": {
            "--variant-containedBg": "var(--variant-textColor)",
          },
          ":disabled": {
            opacity: 0.5,
            backgroundColor: "var(--variant-containedBg)",
            color: "var(--variant-containedColor)",
          },
        },
        outlined: {
          borderWidth: "2px",
          borderColor: "inherit",
          borderStyle: "solid",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 10,
        },
        determinate: {
          borderRadius: 5,
        },
        bar: {
          backgroundColor: palette.darkBlue,
          borderRadius: 5,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        outlined: {
          borderColor: "currentColor !important",
          ".MuiChip-deleteIcon": {
            color: "currentColor !important",
            fill: "currentColor !important",
          },
          ".MuiChip-label": {
            color: "#000 !important",
          },
        },
      },
    },
    MuiModal: {
      defaultProps: {
        disablePortal: true,
      },
    },
    MuiDialog: {
      defaultProps: {
        disablePortal: false,
        disableEscapeKeyDown: true,
        disableScrollLock: false,
      },
    },
    MuiTypography: {
      variants: [
        {
          props: ({ ownerState }) => ownerState.addShadow,
          style: {
            textShadow: `4px 0 37px ${palette.darkBlue}`,
          },
        },
        {
          props: ({ ownerState }) =>
            ownerState.component === "a" ||
            (ownerState.component === LinkWithQuery &&
              !ownerState.overrideTheme),
          style: {
            color: palette.brightBlue,
            fontWeight: "bold",
          },
        },
      ],
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "currentColor",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          display: "none",
        },
      },
    },
  },
  palette: {
    primary: {
      main: palette.brightBlue,
      contrastText: "#ffffff",
    },
    secondary: {
      main: palette.coral,
      contrastText: "#ffffff",
    },
    error: {
      main: palette.red,
      contrastText: "#ffffff",
    },
    grey: {
      main: palette.grey,
      contrastText: "#ffffff",
    },
    background: {
      default: palette.sand,
      paper: "#FFFFFF",
    },
    text: {
      primary: "#000000",
      secondary: palette.darkBlue,
      disabled: palette.grey,
    },
  },
  typography: {
    fontSize: 16,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    fontFamily: ['"Open Sans", sans-serif', '"Comfortaa", sans-serif'].join(
      ",",
    ),
    h1: {
      fontSize: "74px",
      fontFamily: '"Comfortaa", sans-serif',
    },
    h2: {
      fontSize: "48px",
      fontFamily: '"Comfortaa", sans-serif',
    },
    h3: { fontSize: "42px", fontFamily: '"Comfortaa", sans-serif' },
    h4: { fontSize: "32px", fontFamily: '"Comfortaa", sans-serif' },
    h5: { fontSize: "28px", fontFamily: '"Comfortaa", sans-serif' },
    h6: { fontSize: "24px", fontFamily: '"Comfortaa", sans-serif' },
    body1: {
      fontSize: "16px",
      fontFamily: '"Open Sans", sans-serif',
    },
    body2: {
      fontSize: "18px",
      fontFamily: '"Open Sans", sans-serif',
      lineHeight: "140%",
    },
    subtitle1: {
      fontFamily: '"Comfortaa", sans-serif',
      fontSize: "28px",
      fontWeight: 700,
    },
    subtitle2: {
      fontFamily: '"Open Sans", sans-serif',
      fontSize: "21px",
      fontWeight: 600,
      lineHeight: "140%",
    },
    overline: {
      fontFamily: '"Open Sans", sans-serif',
      textTransform: "uppercase",
      fontWeight: 900,
      lineHeight: "normal",
    },
  },
});

theme = createTheme(theme, {
  palette: {
    darkBlue: theme.palette.augmentColor({
      color: {
        main: palette.darkBlue,
        contrastText: "#FFFFFF",
      },
      name: "darkBlue",
    }),
    sand: theme.palette.augmentColor({
      color: { main: palette.sand },
      name: "sand",
    }),
    brightBlue: theme.palette.augmentColor({
      color: { main: palette.brightBlue },
      name: "brightBlue",
    }),
    aqua: theme.palette.augmentColor({
      color: { main: palette.aqua, light: alpha(palette.aqua, 0.2) },
      name: "aqua",
    }),
    lightBlue: theme.palette.augmentColor({
      color: { light: alpha(palette.lightBlue, 0.2), main: palette.lightBlue },
      name: "lightBlue",
    }),
    coral: theme.palette.augmentColor({
      color: { main: palette.coral },
      name: "coral",
    }),
    subtleGrey: theme.palette.augmentColor({
      color: { main: palette.subtleGrey },
      name: "subtleGrey",
    }),
    translucentWhite: theme.palette.augmentColor({
      color: { main: palette.translucentWhite },
      name: "translucentWhite",
    }),
  },
});

export default theme;
